import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileBarChart,
  Download,
  Clock,
  Trash2,
  Plus,
} from 'lucide-react';
import { reportsApi } from '@/api/endpoints';
import { toast } from '@/utils/toast';
import { ConfirmationModal } from '@azyntor-labs/nexus-ui';

interface ReportType {
  type: string;
  name: string;
  description: string;
}

interface ScheduledReport {
  id: string;
  report_type: string;
  frequency: string;
  next_run: string;
  status: string;
  created_at: string;
}

export const ReportsPage = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'generate' | 'scheduled'>('generate');
  const [scheduleModal, setScheduleModal] = useState(false);
  const [scheduleType, setScheduleType] = useState('');
  const [scheduleFrequency, setScheduleFrequency] = useState('weekly');
  const [generatingType, setGeneratingType] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const { data: reportsRes, isLoading: reportsLoading } = useQuery({
    queryKey: ['available-reports'],
    queryFn: () => reportsApi.getAvailableReports(),
    staleTime: 30000,
  });

  const availableReports: ReportType[] = Array.isArray(reportsRes?.data?.data) ? (reportsRes.data.data as ReportType[]) : [];

  const { data: scheduledRes, isLoading: scheduledLoading } = useQuery({
    queryKey: ['scheduled-reports'],
    queryFn: () => reportsApi.getScheduledReports(),
    staleTime: 30000,
  });

  const scheduledReports: ScheduledReport[] = Array.isArray(scheduledRes?.data?.data) ? (scheduledRes.data.data as ScheduledReport[]) : [];

  const handleGenerateReport = async (reportType: string) => {
    setGeneratingType(reportType);
    try {
      const response = await reportsApi.generateReport(reportType);
      const blob = new Blob([response.data as any], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType}-report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report generated');
    } catch {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingType('');
    }
  };

  const scheduleMutation = useMutation({
    mutationFn: () => reportsApi.scheduleReport(scheduleType, { frequency: scheduleFrequency }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
      toast.success('Report scheduled');
      setScheduleModal(false);
      setScheduleType('');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to schedule report'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => reportsApi.deleteScheduledReport(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-reports'] });
      toast.success('Schedule deleted');
      setDeleteConfirmId(null);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to delete schedule'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Reports</h1>
          <p className="text-sm text-white/50 mt-1">Generate and schedule reports</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-white/[0.03] rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('generate')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'generate' ? 'bg-teal-500/20 text-teal-400' : 'text-white/60 hover:text-white/90'
          }`}
        >
          Generate Reports
        </button>
        <button
          onClick={() => setActiveTab('scheduled')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'scheduled' ? 'bg-teal-500/20 text-teal-400' : 'text-white/60 hover:text-white/90'
          }`}
        >
          Scheduled Reports
        </button>
      </div>

      {activeTab === 'generate' && (
        <div>
          {reportsLoading ? (
            <div className="text-center text-white/50 py-8">Loading reports...</div>
          ) : availableReports.length === 0 ? (
            <div className="card-dark p-8 text-center">
              <FileBarChart className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/60">No report types available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableReports.map((report) => (
                <div key={report.type} className="card-dark p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-500/20 flex items-center justify-center">
                      <FileBarChart className="w-5 h-5 text-teal-400" />
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-white/90 mb-1">{report.name}</h3>
                  <p className="text-xs text-white/50 mb-4">{report.description}</p>
                  <button
                    onClick={() => handleGenerateReport(report.type)}
                    disabled={generatingType === report.type}
                    className="btn-primary w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    <span>{generatingType === report.type ? 'Generating...' : 'Generate'}</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'scheduled' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setScheduleModal(true)} className="btn-primary flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
              <Plus className="w-4 h-4" />
              <span>Schedule New</span>
            </button>
          </div>

          <div className="card-dark overflow-hidden">
            {scheduledLoading ? (
              <div className="p-8 text-center text-white/50">Loading scheduled reports...</div>
            ) : scheduledReports.length === 0 ? (
              <div className="p-8 text-center">
                <Clock className="w-12 h-12 text-white/20 mx-auto mb-3" />
                <p className="text-white/60">No scheduled reports</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Report Type</th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Frequency</th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Next Run</th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Status</th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledReports.map((report) => (
                      <tr key={report.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                        <td className="p-3 text-sm text-white/90 capitalize">{report.report_type?.replace(/_/g, ' ')}</td>
                        <td className="p-3 text-sm text-white/80 capitalize">{report.frequency}</td>
                        <td className="p-3 text-sm text-white/70">{report.next_run || '—'}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            report.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/[0.06] text-white/60'
                          }`}>
                            {report.status}
                          </span>
                        </td>
                        <td className="p-3">
                          <button onClick={() => setDeleteConfirmId(report.id)} className="text-red-400 hover:text-red-300">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setScheduleModal(false)}>
          <div className="bg-[#1a1f2e] border border-white/[0.08] rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white/90 mb-4">Schedule Report</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-1">Report Type</label>
                <select value={scheduleType} onChange={(e) => setScheduleType(e.target.value)} className="input-dark w-full">
                  <option value="">-- Select report --</option>
                  {availableReports.map((r) => (
                    <option key={r.type} value={r.type}>{r.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Frequency</label>
                <select value={scheduleFrequency} onChange={(e) => setScheduleFrequency(e.target.value)} className="input-dark w-full">
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setScheduleModal(false)} className="btn-outline px-4 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={() => scheduleMutation.mutate()} disabled={!scheduleType || scheduleMutation.isPending} className="btn-primary px-4 py-2 rounded-lg text-sm disabled:opacity-50">
                {scheduleMutation.isPending ? 'Scheduling...' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmationModal
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={() => deleteConfirmId && deleteMutation.mutate(deleteConfirmId)}
        title="Delete Schedule?"
        message="This scheduled report will be permanently removed."
        variant="danger"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
